import React, { useRef, useState, useEffect } from 'react';
import { useConnectionHealth } from '../../hooks/useConnectionHealth';
import apiClient from '../../api/api';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import 'swiper/css';

const TopBar = ({ shouldShow }) => {
  const [date, setDate] = useState({ day: '', month: '', year: '' });
  const [hotNews, setHotNews] = useState([]);
  const swiperRef = useRef(null);
  const { connectionStatus } = useConnectionHealth();

  const isOnline = connectionStatus === 'connected';
  const dateTone = isOnline ? 'text-green-500' : 'text-red-400';

  const isAgoraUrl = (value) => {
    if (!value) return false;
    const trimmed = String(value).trim();
    if (trimmed.startsWith('/')) return true;
    try {
      const parsed = new URL(trimmed);
      const host = parsed.hostname.toLowerCase();
      return host === 'agoraweb.io' || host.endsWith('.agoraweb.io');
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const today = new Date();
    const options = { month: 'long' };
    setDate({
      day: today.getDate(),
      month: new Intl.DateTimeFormat('es-ES', options).format(today),
      year: today.getFullYear(),
    });
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchHotNews = async () => {
      let tickerItems = [];
      let editorialItems = [];
      let adItems = [];
      try {
        const adsResponse = await apiClient.get('/ads/public', {
          params: { placement: 'top_nav_hot_news' },
        });
        const slaActive = adsResponse.data?.data?.slaStatus?.active !== false;
        if (slaActive) {
          const ads = Array.isArray(adsResponse.data?.data?.ads)
            ? adsResponse.data.data.ads
            : [];
          adItems = ads
            .filter((ad) => ad?.isSovereign || ad?.approvalLevel === 'vip')
            .map((ad) => ({
              text: ad?.content,
              url: ad?.linkUrl || ad?.actionValue || ad?.internalPath || '',
            }))
            .filter((item) => typeof item.text === 'string' && item.text.trim());
        }
      } catch {
        adItems = [];
      }

      try {
        const response = await apiClient.get('/editorial-news');
        const items = response.data?.data?.items || [];
        editorialItems = items
          .map((item) => ({
            text: item?.text,
            url: item?.url,
          }))
          .filter((item) => typeof item.text === 'string' && item.text.trim())
          .slice(0, 4);
      } catch {
        editorialItems = [];
      }

      if (adItems.length > 10) {
        tickerItems = adItems.slice(0, 4);
      } else {
        tickerItems = [...adItems, ...editorialItems].slice(0, 4);
      }

      if (isMounted) {
        setHotNews(tickerItems);
      }
    };
    fetchHotNews();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (swiperRef.current && hotNews.length > 1) {
      swiperRef.current.slideTo(0, 0);
      if (swiperRef.current.autoplay?.start) {
        swiperRef.current.autoplay.start();
      }
    }
  }, [hotNews.length]);

  return (
    <div
      className={`bg-stone-800 text-gray-300 text-xs py-2 px-4 h-15 flex items-center ${
        shouldShow ? 'translate-y-0' : ''
      }`}
    >
      <div className="container mx-auto flex justify-between items-center">
        <div className="flex items-center flex-grow">
          <div className="pr-4 mr-4 border-r border-gray-700">
            <div className={`text-3xl font-semibold ${dateTone}`}>{date.day}</div>
            <div className={`text-xs uppercase ${dateTone}`}>
              {date.month} {date.year}
            </div>
          </div>

          {hotNews.length > 0 && (
            <div className="hidden md:flex items-center flex-grow relative max-w-2xl">
              <span className="font-bold text-white mr-3 py-1 px-2 bg-red-600 text-xs whitespace-nowrap">
                Titulares:
              </span>
              
              {/* Contenedor con espacio reservado a la derecha para las flechas */}
              <div className="flex items-center flex-grow overflow-hidden pr-20">
                <Swiper
                  key={`ticker-${hotNews.length}`}
                  modules={[Autoplay]}
                  direction="vertical"
                  loop={true}
                  autoplay={{ delay: 7000, disableOnInteraction: false }}
                  className="h-6 w-full"
                  onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                  }}
                >
                  {hotNews.map((news, index) => (
                    <SwiperSlide key={index} className="flex items-center">
                      {news.url ? (
                        <a
                          href={news.url}
                          target={isAgoraUrl(news.url) ? '_self' : '_blank'}
                          rel={isAgoraUrl(news.url) ? undefined : 'noopener noreferrer'}
                          className="hover:text-white transition-colors truncate block"
                        >
                          {news.text}
                        </a>
                      ) : (
                        <span className="text-gray-200 truncate block">{news.text}</span>
                      )}
                    </SwiperSlide>
                  ))}
                </Swiper>
              </div>

              {/* Botones fijos a la derecha */}
              <div className="absolute right-0 flex gap-1 z-10">
                <button
                  type="button"
                  onClick={() => swiperRef.current?.slidePrev()}
                  className="w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-red-600 transition"
                  title="Anterior"
                >
                  <i className="fas fa-chevron-left text-[10px]"></i>
                </button>
                <button
                  type="button"
                  onClick={() => swiperRef.current?.slideNext()}
                  className="w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-red-600 transition"
                  title="Siguiente"
                >
                  <i className="fas fa-chevron-right text-[10px]"></i>
                </button>
              </div>
            </div>
          )}
        </div>

        <ul className="flex space-x-3 ml-4">
          <li><a href="#" className="hover:text-white"><i className="fab fa-facebook-f"></i></a></li>
          <li><a href="#" className="hover:text-white"><i className="fab fa-twitter"></i></a></li>
          <li><a href="#" className="hover:text-white"><i className="fab fa-instagram"></i></a></li>
        </ul>
      </div>
    </div>
  );
};

export default TopBar;